import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronRight, X, Dumbbell, Clock, Check, Moon, HeartPulse, Droplets, Camera, Pill, Scale } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { toast } from 'sonner';

const API = '/api';

const C = {
  bg: '#FDFBF7', cream: '#F5F0E8', card: '#FFFFFF', border: '#E8E3DB',
  text: '#1A1A18', textSec: '#6A6560', muted: '#9A9590',
  accent: '#C67A5C', green: '#5A8C6A', black: '#1A1A18',
};

const LOG_ACTIONS = [
  { id: 'eat', emoji: '\uD83C\uDF7D', label: 'What to Eat', path: '/what-to-eat' },
  { id: 'cook', emoji: '\uD83E\uDDD1\u200D\uD83C\uDF73', label: 'Cook', path: '/cooking-mode' },
  { id: 'beverage', emoji: '\uD83E\uDD64', label: 'Beverage', path: '/beverages' },
  { id: 'exercise', emoji: '\uD83C\uDFC3', label: 'Exercise', modal: 'exercise' },
  { id: 'sleep', emoji: '\uD83D\uDE34', label: 'Sleep', modal: 'sleep' },
  { id: 'fasting', emoji: '\u23F0', label: 'Fasting', path: '/fasting' },
];

const TRACK_ACTIONS = [
  { id: 'water', icon: Droplets, label: 'Hydration', action: 'water', toggleKey: 'hydration' },
  { id: 'weight', icon: Scale, label: 'Weight', modal: 'weight', toggleKey: 'weight_tracking' },
  { id: 'symptoms', icon: HeartPulse, label: 'Symptoms', path: '/symptom-journal', toggleKey: 'symptoms' },
  { id: 'medication', icon: Pill, label: 'Medication', path: '/medications', toggleKey: 'medications' },
  { id: 'progress-photo', icon: Camera, label: 'Progress Photo', path: '/progress-photos', toggleKey: 'progress_photos' },
];

import { UniversalWorkoutModal } from '@/components/UniversalWorkoutModal';

/* ── Sleep Modal ── */
function SleepPopup({ onClose }) {
  const { authHeaders } = useAuth();
  const [hours, setHours] = useState('');
  const [quality, setQuality] = useState('');
  const [saving, setSaving] = useState(false);

  const qualityMap = { poor: 1, fair: 2, good: 3, great: 4 };

  const handleLog = async () => {
    if (!hours) return;
    setSaving(true);
    try {
      await axios.post(`${API}/sleep/log`, {
        sleep_hours: parseFloat(hours),
        quality: qualityMap[quality] || 2,
        date: new Date().toISOString().split('T')[0],
      }, authHeaders());
      toast.success('Sleep logged!');
      onClose();
    } catch { toast.error('Failed to log sleep'); }
    setSaving(false);
  };

  return (
    <div style={{ padding: '20px 18px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <p style={{ fontSize: 20, fontWeight: 400, margin: 0, color: C.text, fontFamily: 'var(--font-serif)' }}>Log sleep</p>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}><X className="w-4 h-4" style={{ color: C.muted }} /></button>
      </div>
      <p style={{ fontSize: 13, margin: '0 0 10px', color: C.textSec, fontFamily: 'var(--font-sans)' }}>How long did you sleep?</p>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <input data-testid="qa-sleep-hours" type="number" step="0.5" min="0" max="24" value={hours} onChange={e => setHours(e.target.value)} placeholder="7.5" style={{
          width: 80, padding: '10px 12px', borderRadius: 12, border: `1px solid ${C.border}`,
          background: C.card, fontSize: 16, fontFamily: 'var(--font-sans)', color: C.text, textAlign: 'center', outline: 'none',
        }} />
        <span style={{ fontSize: 14, color: C.muted, fontFamily: 'var(--font-sans)' }}>hours</span>
      </div>
      <p style={{ fontSize: 13, margin: '0 0 10px', color: C.textSec, fontFamily: 'var(--font-sans)' }}>How was the quality?</p>
      <div style={{ display: 'flex', gap: 6, marginBottom: 20 }}>
        {['poor', 'fair', 'good', 'great'].map(q => (
          <button key={q} onClick={() => setQuality(q)} style={{
            flex: 1, padding: '8px 4px', borderRadius: 20, border: 'none', cursor: 'pointer',
            background: quality === q ? C.black : C.cream,
            color: quality === q ? '#FDFBF7' : C.textSec,
            fontSize: 12, fontWeight: 500, fontFamily: 'var(--font-sans)', textTransform: 'capitalize',
          }}>{q}</button>
        ))}
      </div>
      <button data-testid="qa-sleep-submit" onClick={handleLog} disabled={saving || !hours} style={{
        width: '100%', padding: '12px', borderRadius: 28, border: 'none',
        background: hours ? C.black : C.border, color: hours ? '#FDFBF7' : C.muted,
        fontSize: 14, fontWeight: 500, cursor: hours ? 'pointer' : 'default',
        fontFamily: 'var(--font-sans)', opacity: saving ? 0.7 : 1,
      }}>{saving ? 'Saving...' : 'Log sleep'}</button>
    </div>
  );
}

/* ── Weight Modal ── */
function WeightPopup({ onClose }) {
  const { authHeaders } = useAuth();
  const [weight, setWeight] = useState('');
  const [saving, setSaving] = useState(false);

  const handleLog = async () => {
    const lbs = parseFloat(weight);
    if (!lbs || lbs < 50 || lbs > 700) return;
    setSaving(true);
    try {
      const kg = Math.round(lbs / 2.205 * 10) / 10;
      await axios.post(`${API}/weight/log`, { weight_kg: kg }, authHeaders());
      toast.success(`Weight logged: ${lbs} lbs`);
      onClose();
    } catch { toast.error('Failed to log weight'); }
    setSaving(false);
  };

  return (
    <div style={{ padding: '20px 18px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <p style={{ fontSize: 20, fontWeight: 400, margin: 0, color: C.text, fontFamily: 'var(--font-serif)' }}>Log weight</p>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}><X className="w-4 h-4" style={{ color: C.muted }} /></button>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
        <input data-testid="qa-weight-input" type="number" step="0.1" value={weight} onChange={e => setWeight(e.target.value)} placeholder="175" style={{
          width: 100, padding: '12px 14px', borderRadius: 12, border: `1px solid ${C.border}`,
          background: C.card, fontSize: 18, fontFamily: 'var(--font-sans)', color: C.text, textAlign: 'center', outline: 'none',
        }} />
        <span style={{ fontSize: 14, color: C.muted, fontFamily: 'var(--font-sans)' }}>lbs</span>
      </div>
      <button data-testid="qa-weight-submit" onClick={handleLog} disabled={saving || !weight} style={{
        width: '100%', padding: '12px', borderRadius: 28, border: 'none',
        background: weight ? C.green : C.border, color: weight ? '#FFF' : C.muted,
        fontSize: 14, fontWeight: 500, cursor: weight ? 'pointer' : 'default',
        fontFamily: 'var(--font-sans)', opacity: saving ? 0.7 : 1,
      }}>{saving ? 'Saving...' : 'Save weight'}</button>
    </div>
  );
}

const SIDEBAR_THEME = {
  light:    { bg: '#507030', fabBg: '#507030' },
  dark:     { bg: '#2a3a20', fabBg: '#2a3a20' },
  forest:   { bg: '#3a5c28', fabBg: '#3a5c28' },
  ocean:    { bg: '#1a5c6a', fabBg: '#1a5c6a' },
  sunset:   { bg: '#7a5030', fabBg: '#7a5030' },
  midnight: { bg: '#1a2e20', fabBg: '#1a2e20' },
};

/* ══════════ Main Sidebar Component ══════════ */
export function QuickActionSidebar() {
  const [expanded, setExpanded] = useState(false);
  const [activeModal, setActiveModal] = useState(null);
  const [toggles, setToggles] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { authHeaders } = useAuth();
  const { theme } = useTheme();
  const st = SIDEBAR_THEME[theme] || SIDEBAR_THEME.light;

  // Fetch health tool toggles once
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/health-tools/toggles`, authHeaders());
        setToggles(res.data.toggles);
      } catch {}
    })();
  }, [authHeaders]);

  const visibleTrackActions = TRACK_ACTIONS.filter(a => !toggles || toggles[a.toggleKey] !== false);

  // Listen for weight modal open events from Health Tools cards
  useEffect(() => {
    const openWeight = () => setActiveModal('weight');
    window.addEventListener('nuri-open-weight', openWeight);
    return () => window.removeEventListener('nuri-open-weight', openWeight);
  }, []);

  const handleAction = async (item) => {
    setExpanded(false);
    if (item.id === 'voice') {
      window.dispatchEvent(new CustomEvent('nuri-open-voice'));
    } else if (item.path) {
      navigate(item.path);
    } else if (item.action === 'water') {
      try {
        await axios.post(`${API}/water/log`, { amount_ml: 250, beverage_type: 'water' }, authHeaders());
        toast.success('+1 cup of water logged (250ml)');
        window.dispatchEvent(new Event('water-logged'));
      } catch { toast.error('Failed to log water'); }
    } else if (item.modal) {
      setActiveModal(item.modal);
    }
  };

  const closeModal = useCallback(() => {
    setActiveModal(null);
    window.dispatchEvent(new CustomEvent('nuri-data-refresh'));
  }, []);

  const hiddenRoutes = ['/', '/onboarding'];
  if (hiddenRoutes.includes(location.pathname)) return null;

  return (
    <>
      {/* Universal Workout Modal (portal-rendered) */}
      <UniversalWorkoutModal isOpen={activeModal === 'exercise'} onClose={closeModal} onLogged={closeModal} />

      {/* Modal overlay for sleep/weight */}
      {activeModal && activeModal !== 'exercise' && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 210, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 20px' }}>
          <div onClick={closeModal} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(2px)' }} />
          <div style={{
            position: 'relative', width: '100%', maxWidth: 400,
            background: C.bg, borderRadius: 24, border: `1px solid ${C.border}`,
            boxShadow: '0 16px 48px rgba(0,0,0,0.15)', overflow: 'hidden',
          }}>
            {activeModal === 'sleep' && <SleepPopup onClose={closeModal} />}
            {activeModal === 'weight' && <WeightPopup onClose={closeModal} />}
          </div>
        </div>
      )}

      {/* Sidebar FAB */}
      <div data-testid="quick-action-sidebar" style={{
        position: 'fixed', right: 12, bottom: 100, zIndex: 110,
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0,
      }}>
        {expanded && (
          <div style={{
            background: st.bg, border: '1px solid rgba(255,255,255,0.15)',
            borderRadius: 20, padding: '8px 6px',
            display: 'flex', flexDirection: 'column', gap: 2,
            marginBottom: 6, boxShadow: '0 4px 24px rgba(0,0,0,0.2)',
            maxHeight: '65vh', overflowY: 'auto',
          }}>
            {/* Log section */}
            <p style={{ fontSize: 9, color: 'rgba(255,255,255,0.45)', fontFamily: 'var(--font-sans)', textTransform: 'uppercase', letterSpacing: 1, padding: '4px 12px 2px', margin: 0 }}>Log</p>
            {LOG_ACTIONS.map(a => (
              <button key={a.id} data-testid={`qa-${a.id}`} onClick={() => handleAction(a)} title={a.label}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px 6px 8px',
                  borderRadius: 12, border: 'none', background: 'rgba(255,255,255,0.12)',
                  cursor: 'pointer', transition: 'all 0.15s', minWidth: 120,
                }}>
                <span style={{ fontSize: 18, lineHeight: 1 }}>{a.emoji}</span>
                <span style={{ fontSize: 11, fontWeight: 500, color: '#FDFBF7', whiteSpace: 'nowrap', letterSpacing: '0.2px' }}>{a.label}</span>
              </button>
            ))}
            {/* Track section */}
            {visibleTrackActions.length > 0 && (
              <>
                <p style={{ fontSize: 9, color: 'rgba(255,255,255,0.45)', fontFamily: 'var(--font-sans)', textTransform: 'uppercase', letterSpacing: 1, padding: '6px 12px 2px', margin: 0 }}>Track</p>
                {visibleTrackActions.map(a => {
                  const Icon = a.icon;
                  return (
                    <button key={a.id} data-testid={`qa-${a.id}`} onClick={() => handleAction(a)} title={a.label}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px 6px 10px',
                        borderRadius: 12, border: 'none', background: 'rgba(255,255,255,0.12)',
                        cursor: 'pointer', transition: 'all 0.15s', minWidth: 120,
                      }}>
                      <Icon style={{ width: 16, height: 16, color: '#FDFBF7' }} />
                      <span style={{ fontSize: 11, fontWeight: 500, color: '#FDFBF7', whiteSpace: 'nowrap', letterSpacing: '0.2px' }}>{a.label}</span>
                    </button>
                  );
                })}
              </>
            )}
          </div>
        )}
        <button
          data-testid="qa-toggle"
          onClick={() => setExpanded(!expanded)}
          style={{
            width: 40, height: 40, borderRadius: '50%',
            background: st.fabBg, border: '1px solid rgba(255,255,255,0.15)',
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 2px 12px rgba(0,0,0,0.18)',
            fontSize: expanded ? 14 : 20, color: '#FDFBF7',
          }}
        >
          {expanded ? <ChevronRight className="w-4 h-4" /> : '+'}
        </button>
      </div>
    </>
  );
}
